'use client';

import React, { useState, useMemo } from 'react';
import {
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Eye,
  Edit2,
  Trash2,
  CheckSquare,
  Square,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import { toast } from 'sonner';

// Backend integration point: GET /api/ehs/incidents?page=1&limit=10&filters=...
const allIncidents = [
  {
    id: 'INC-2026-0412',
    type: 'Chemical Exposure',
    department: 'Chemical Proc.',
    site: 'Houston TX',
    severity: 'critical' as const,
    recordable: true,
    rcaStatus: 'in-progress' as const,
    caCount: 3,
    reportedBy: 'Priya Nair',
    date: '04/25/2026',
    description: 'Chemical splash to eye — PPE failure',
    ltiDays: 2,
  },
  {
    id: 'NM-2026-0389',
    type: 'Vehicle / Equipment',
    department: 'Logistics',
    site: 'Houston TX',
    severity: 'high' as const,
    recordable: false,
    rcaStatus: 'open' as const,
    caCount: 1,
    reportedBy: 'Darnell Washington',
    date: '04/24/2026',
    description: 'Forklift near-pedestrian contact in aisle 7',
    ltiDays: 0,
  },
  {
    id: 'INC-2026-0411',
    type: 'Slip / Trip / Fall',
    department: 'Maintenance',
    site: 'Baton Rouge LA',
    severity: 'medium' as const,
    recordable: true,
    rcaStatus: 'closed' as const,
    caCount: 2,
    reportedBy: 'Elena Vasquez',
    date: '04/23/2026',
    description: 'Slip on wet floor — minor laceration to forearm',
    ltiDays: 1,
  },
  {
    id: 'NM-2026-0388',
    type: 'Pressure / Process',
    department: 'Operations',
    site: 'Corpus Christi TX',
    severity: 'high' as const,
    recordable: false,
    rcaStatus: 'in-progress' as const,
    caCount: 4,
    reportedBy: 'James Okafor',
    date: '04/23/2026',
    description: 'Valve pressure relief unexpected activation',
    ltiDays: 0,
  },
  {
    id: 'INC-2026-0410',
    type: 'Electrical',
    department: 'Electrical',
    site: 'Denver CO',
    severity: 'high' as const,
    recordable: true,
    rcaStatus: 'in-progress' as const,
    caCount: 2,
    reportedBy: 'Mei-Lin Chen',
    date: '04/22/2026',
    description: 'Arc flash event during panel maintenance — no injury',
    ltiDays: 0,
  },
  {
    id: 'NM-2026-0387',
    type: 'Struck By / Against',
    department: 'Construction',
    site: 'Houston TX',
    severity: 'medium' as const,
    recordable: false,
    rcaStatus: 'open' as const,
    caCount: 1,
    reportedBy: 'Tobias Müller',
    date: '04/21/2026',
    description: 'Tool dropped from 12ft platform — no personnel below',
    ltiDays: 0,
  },
  {
    id: 'INC-2026-0409',
    type: 'Ergonomic / Strain',
    department: 'Maintenance',
    site: 'Baton Rouge LA',
    severity: 'low' as const,
    recordable: true,
    rcaStatus: 'closed' as const,
    caCount: 1,
    reportedBy: 'Ananya Krishnan',
    date: '04/19/2026',
    description: 'Lower back strain during pump assembly',
    ltiDays: 3,
  },
  {
    id: 'NM-2026-0386',
    type: 'Fire / Explosion',
    department: 'Chemical Proc.',
    site: 'Corpus Christi TX',
    severity: 'critical' as const,
    recordable: false,
    rcaStatus: 'in-progress' as const,
    caCount: 5,
    reportedBy: 'Marcus Reyes',
    date: '04/18/2026',
    description: 'Solvent vapors ignited near ignition source — contained',
    ltiDays: 0,
  },
  {
    id: 'INC-2026-0408',
    type: 'Slip / Trip / Fall',
    department: 'Logistics',
    site: 'Denver CO',
    severity: 'medium' as const,
    recordable: true,
    rcaStatus: 'closed' as const,
    caCount: 2,
    reportedBy: 'Fatima Al-Rashid',
    date: '04/16/2026',
    description: 'Trip over unguarded cable run — bruised knee',
    ltiDays: 0,
  },
  {
    id: 'NM-2026-0385',
    type: 'Chemical Exposure',
    department: 'Chemical Proc.',
    site: 'Houston TX',
    severity: 'high' as const,
    recordable: false,
    rcaStatus: 'open' as const,
    caCount: 2,
    reportedBy: 'Samuel Osei',
    date: '04/15/2026',
    description: 'Ammonia leak detected — area evacuated, no exposure',
    ltiDays: 0,
  },
  {
    id: 'INC-2026-0407',
    type: 'Struck By / Against',
    department: 'Operations',
    site: 'Houston TX',
    severity: 'high' as const,
    recordable: true,
    rcaStatus: 'closed' as const,
    caCount: 3,
    reportedBy: 'Ingrid Svensson',
    date: '04/14/2026',
    description: 'Struck by swinging gate — shoulder contusion',
    ltiDays: 2,
  },
  {
    id: 'NM-2026-0384',
    type: 'Confined Space',
    department: 'Maintenance',
    site: 'Baton Rouge LA',
    severity: 'critical' as const,
    recordable: false,
    rcaStatus: 'in-progress' as const,
    caCount: 4,
    reportedBy: 'David Okonkwo',
    date: '04/12/2026',
    description: 'Atmospheric monitor alarm in tank — work stopped',
    ltiDays: 0,
  },
];

type SortKey = keyof typeof allIncidents[0];
type SortDir = 'asc' | 'desc';

const PAGE_SIZE_OPTIONS = [5, 10, 20];

export default function IncidentDataTable() {
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const sorted = useMemo(() => {
    return [...allIncidents].sort((a, b) => {
      const av = a[sortKey] as string | number | boolean;
      const bv = b[sortKey] as string | number | boolean;
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [sortKey, sortDir]);

  const totalPages = Math.ceil(sorted.length / pageSize);
  const paginated = sorted.slice((page - 1) * pageSize, page * pageSize);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const toggleRow = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === paginated.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(paginated.map((r) => r.id)));
    }
  };

  const handleBulkDelete = () => {
    // TODO: Backend integration — DELETE /api/ehs/incidents with ids array
    toast.success(`${selected.size} incident${selected.size > 1 ? 's' : ''} deleted`, {
      description: 'Corrective actions linked to these incidents remain active.',
    });
    setSelected(new Set());
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ChevronsUpDown size={12} className="opacity-40" />;
    return sortDir === 'asc' ? (
      <ChevronUp size={12} className="text-[hsl(var(--primary))]" />
    ) : (
      <ChevronDown size={12} className="text-[hsl(var(--primary))]" />
    );
  };

  const columns: { key: SortKey; label: string; width?: string }[] = [
    { key: 'id', label: 'Incident ID', width: 'w-32' },
    { key: 'type', label: 'Type', width: 'w-36' },
    { key: 'department', label: 'Department', width: 'w-32' },
    { key: 'site', label: 'Site', width: 'w-28' },
    { key: 'severity', label: 'Severity', width: 'w-24' },
    { key: 'recordable', label: 'Recordable', width: 'w-24' },
    { key: 'rcaStatus', label: 'RCA Status', width: 'w-28' },
    { key: 'caCount', label: 'Open CAs', width: 'w-20' },
    { key: 'reportedBy', label: 'Reported By', width: 'w-32' },
    { key: 'date', label: 'Date', width: 'w-24' },
  ];

  return (
    <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
      {/* Table Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-[hsl(var(--border))]">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-600 text-[hsl(var(--foreground))]">
            Incident & Near-Miss Register
          </h3>
          <span className="text-[11px] font-600 px-2 py-0.5 rounded-md bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] tabular-nums">
            {allIncidents.length} records
          </span>
        </div>
      </div>

      {/* Bulk action bar */}
      <div
        className={`overflow-hidden transition-all duration-200 ${selected.size > 0 ? 'max-h-12' : 'max-h-0'}`}
      >
        <div className="flex items-center gap-3 px-5 py-2.5 bg-[hsl(var(--primary)/0.08)] border-b border-[hsl(var(--border))]">
          <span className="text-xs font-600 text-[hsl(var(--primary))]">
            {selected.size} selected
          </span>
          <button
            onClick={handleBulkDelete}
            className="flex items-center gap-1.5 text-xs text-[hsl(var(--negative))] hover:underline"
          >
            <Trash2 size={12} />
            Delete selected
          </button>
          <button
            onClick={() => setSelected(new Set())}
            className="ml-auto text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
          >
            Clear selection
          </button>
        </div>
      </div>

      {/* Scrollable table */}
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm min-w-[1100px]">
          <thead>
            <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.4)]">
              <th className="w-10 px-4 py-3 text-left">
                <button onClick={toggleAll} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]">
                  {selected.size === paginated.length && paginated.length > 0 ? (
                    <CheckSquare size={14} className="text-[hsl(var(--primary))]" />
                  ) : (
                    <Square size={14} />
                  )}
                </button>
              </th>
              {columns.map((col) => (
                <th
                  key={`th-${col.key}`}
                  className={`px-3 py-3 text-left text-[11px] font-600 uppercase tracking-wider text-[hsl(var(--muted-foreground))] cursor-pointer hover:text-[hsl(var(--foreground))] select-none ${col.width ?? ''}`}
                  onClick={() => toggleSort(col.key)}
                >
                  <div className="flex items-center gap-1">
                    {col.label}
                    <SortIcon col={col.key} />
                  </div>
                </th>
              ))}
              <th className="px-3 py-3 text-right text-[11px] font-600 uppercase tracking-wider text-[hsl(var(--muted-foreground))] w-24">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((row, idx) => (
              <tr
                key={row.id}
                className={`
                  border-b border-[hsl(var(--border))] last:border-0 transition-colors duration-100
                  hover:bg-[hsl(var(--muted)/0.5)]
                  ${idx % 2 === 0 ? '' : 'bg-[hsl(var(--muted)/0.2)]'}
                  ${selected.has(row.id) ? 'bg-[hsl(var(--primary)/0.05)]' : ''}
                `}
              >
                <td className="px-4 py-3">
                  <button
                    onClick={() => toggleRow(row.id)}
                    className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                  >
                    {selected.has(row.id) ? (
                      <CheckSquare size={14} className="text-[hsl(var(--primary))]" />
                    ) : (
                      <Square size={14} />
                    )}
                  </button>
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-1.5">
                    {row.type === 'Chemical Exposure' || row.type === 'Fire / Explosion' || row.type === 'Confined Space' ? (
                      <AlertTriangle size={11} className="text-[hsl(var(--negative))] shrink-0" />
                    ) : null}
                    <span className="font-mono text-xs font-500 text-[hsl(var(--foreground))] tabular-nums">
                      {row.id}
                    </span>
                  </div>
                </td>
                <td className="px-3 py-3 text-xs text-[hsl(var(--foreground))]">{row.type}</td>
                <td className="px-3 py-3 text-xs text-[hsl(var(--foreground))]">{row.department}</td>
                <td className="px-3 py-3 text-xs text-[hsl(var(--foreground))]">{row.site}</td>
                <td className="px-3 py-3">
                  <StatusBadge variant={row.severity} label={row.severity.charAt(0).toUpperCase() + row.severity.slice(1)} />
                </td>
                <td className="px-3 py-3">
                  <StatusBadge
                    variant={row.recordable ? 'recordable' : 'non-recordable'}
                    label={row.recordable ? 'Recordable' : 'Non-rec.'}
                  />
                </td>
                <td className="px-3 py-3">
                  <StatusBadge
                    variant={row.rcaStatus as any}
                    label={
                      row.rcaStatus === 'in-progress' ?'In Progress'
                        : row.rcaStatus.charAt(0).toUpperCase() + row.rcaStatus.slice(1)
                    }
                  />
                </td>
                <td className="px-3 py-3 text-center">
                  <span
                    className={`text-xs font-700 tabular-nums ${
                      row.caCount > 3
                        ? 'text-[hsl(var(--negative))]'
                        : row.caCount > 1
                        ? 'text-amber-500' :'text-[hsl(var(--muted-foreground))]'
                    }`}
                  >
                    {row.caCount}
                  </span>
                </td>
                <td className="px-3 py-3 text-xs text-[hsl(var(--foreground))]">{row.reportedBy}</td>
                <td className="px-3 py-3 text-xs font-mono text-[hsl(var(--muted-foreground))] tabular-nums">{row.date}</td>
                <td className="px-3 py-3">
                  <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      title="View incident details"
                      className="p-1.5 rounded-md hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-all duration-150"
                    >
                      <Eye size={13} />
                    </button>
                    <button
                      title="Edit incident record"
                      className="p-1.5 rounded-md hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-all duration-150"
                    >
                      <Edit2 size={13} />
                    </button>
                    <button
                      title="Delete incident — this cannot be undone"
                      onClick={() => {
                        // TODO: Backend integration — DELETE /api/ehs/incidents/:id
                        toast.error('Delete incident?', {
                          description: `${row.id} — This action cannot be undone.`,
                          action: { label: 'Confirm', onClick: () => toast.success('Incident deleted') },
                        });
                      }}
                      className="p-1.5 rounded-md hover:bg-red-500/10 text-[hsl(var(--muted-foreground))] hover:text-red-500 transition-all duration-150"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-5 py-3.5 border-t border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.3)]">
        <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
          <span>Rows per page:</span>
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-md px-2 py-1 text-xs text-[hsl(var(--foreground))] outline-none"
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={`ps-${n}`} value={n}>{n}</option>
            ))}
          </select>
          <span className="tabular-nums">
            {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, allIncidents.length)} of {allIncidents.length}
          </span>
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="p-1.5 rounded-md hover:bg-[hsl(var(--muted))] disabled:opacity-40 disabled:cursor-not-allowed text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-all"
          >
            <ChevronLeft size={14} />
          </button>
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={`page-${i + 1}`}
              onClick={() => setPage(i + 1)}
              className={`w-7 h-7 rounded-md text-xs font-600 tabular-nums transition-all ${
                page === i + 1
                  ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]'
                  : 'hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]'
              }`}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="p-1.5 rounded-md hover:bg-[hsl(var(--muted))] disabled:opacity-40 disabled:cursor-not-allowed text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-all"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}